import { AppLayout } from '@/components/layouts/AppLayout'
import { PartMatchingTable } from '@/components/organisms/PartMatchingTable'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { updateCaseStatus, useCaseDetail } from '@/hooks/api/useCaseDetail'
import { useCaseSyncListener } from '@/hooks/useCaseSyncListener'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/lib/utils'
import { syncCase } from '@/services/cases'
import { useAppSelector } from '@/store/hooks'
import { format } from 'date-fns'
import { ArrowLeft, ArrowRight, Calendar, Car, CheckCircle, Database, RefreshCw, Undo2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const CaseDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false)
  const [reasonText, setReasonText] = useState('')
  const [isStatusLoading, setStatusLoading] = useState(false)

  const { data: caseDetail, isLoading: isFetching } = useCaseDetail(id)
  const { user } = useAppSelector(state => state.auth)
  const isAdmin = user?.roles?.includes('admin') ?? false

  const sync = useCaseSyncListener(caseDetail?.id)
  const [isSyncPending, setIsSyncPending] = useState(false)
  const syncBaselineRef = useRef(0)

  useDocumentTitle(caseDetail?.registrationNumber ?? caseDetail?.id)

  const handleUpdateStatus = useCallback(async () => {
    setStatusLoading(true)
    const newStatus = caseDetail?.status === 'active' ? 'archived' : 'active'
    await updateCaseStatus(caseDetail.id, newStatus, reasonText)
    setStatusLoading(false)
    setIsStatusDialogOpen(false)
  }, [caseDetail, reasonText])

  const handleSync = useCallback(async () => {
    if (!caseDetail) return
    // Remember what's in RTDB now; the sync is "done" once a newer value lands.
    syncBaselineRef.current = sync?.updatedAt ?? 0
    setIsSyncPending(true)
    try {
      await syncCase(caseDetail.id)
    } catch {
      // Enqueue failed — clear immediately; on success we wait for the RTDB signal.
      setIsSyncPending(false)
    }
  }, [caseDetail, sync])

  // Clear the spinner + refetch when the sync we started reports a terminal status
  // (a value newer than what existed at click time — server timestamps on both sides).
  useEffect(() => {
    if (!caseDetail || !isSyncPending || !sync) return
    if (sync.updatedAt > syncBaselineRef.current && sync.status !== 'syncing') {
      setIsSyncPending(false)
      if (sync.status === 'done') useCaseDetail.invalidate(caseDetail.id)
    }
  }, [sync, isSyncPending, caseDetail])

  const isSyncing = isSyncPending

  if (isFetching) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-64" />
        </div>
      </AppLayout>
    )
  }

  if (!caseDetail) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Matchning hittades inte</p>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/cases')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka till Delcheck
          </Button>

          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="outline" onClick={handleSync} disabled={isSyncing}>
                <RefreshCw className={cn('h-4 w-4 mr-2', isSyncing && 'animate-spin')} />
                {isSyncing ? 'Synkar…' : 'Synka fakturor'}
              </Button>
            )}

            {isAdmin && caseDetail.cabasEstimateId && (
              <Button variant="outline" onClick={() => navigate(`/cabas-sync/${caseDetail.cabasEstimateId}`)}>
                <Database className="h-4 w-4 mr-2" />
                Visa Cabas-data
              </Button>
            )}

            {caseDetail.status === 'active' ? (
              <Button onClick={() => setIsStatusDialogOpen(true)}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Markera som klar
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => setIsStatusDialogOpen(true)}>
                <Undo2 className="h-4 w-4 mr-2" />
                Ångra
              </Button>
            )}
          </div>
        </div>

        <div>
          <h1 className="text-3xl font-bold">Matchning {caseDetail.id}</h1>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Car className="h-4 w-4" />
                Bil
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center">
                <p className="text-xl font-semibold">{caseDetail.registrationNumber}</p>
                {caseDetail.carId && (
                  <Button variant="link" size="sm" onClick={() => navigate(`/cars/${caseDetail.carId}`)}>
                    <ArrowRight className="h-4 w-4 mr-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Senast uppdaterad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xl font-semibold">{format(new Date(caseDetail.updatedAt), 'yyyy-MM-dd HH:mm')}</p>
            </CardContent>
          </Card>
        </div>

        <PartMatchingTable
          cabasLines={caseDetail.cabasLines}
          invoiceLines={caseDetail.invoiceLines}
          matched={caseDetail.matchedItems}
        />
      </div>
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{caseDetail.status === 'active' ? 'Markera matchning som klar' : 'Ångra'}</DialogTitle>
            <DialogDescription>Lägg till eventuell extra information om matchningen (frivilligt).</DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Skriv eventuella kommentarer här..."
            value={reasonText}
            onChange={e => setReasonText(e.target.value)}
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusDialogOpen(false)}>
              Avbryt
            </Button>
            <Button disabled={isStatusLoading} onClick={handleUpdateStatus}>
              Bekräfta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}

export default CaseDetail
